import { createSlice } from '@reduxjs/toolkit';
import qs from 'qs';
import { ROUTES } from '../../config/routes';
import { tryToParseRouteFromString } from '../../contexts/UserProvider';

// Helper functions
const getParsedBreadcrumbs = (search, pathname, breadcrumbs) => {
  const parsedSearch = search && qs.parse(search, { ignoreQueryPrefix: true });

  if (pathname) {
    // Try to parse route from pathname if needed
    // This will be handled by tryToParseRouteFromString equivalent
    const parsedRoute = tryToParseRouteFromString(pathname, ROUTES);
    return getBreadcrumbs(breadcrumbs, parsedRoute, false);
  }

  const bcData = parsedSearch?.bc ? JSON.parse(atob(parsedSearch.bc)) : [];

  // Filter out any undefined/null items
  return bcData.filter(item => item && item.path);
};

const getBreadcrumbs = (currentBreadcrumbs, newItem, back) => {
  // Clear array and set only first item
  if (newItem.clearBreadcrumbs || !newItem.path) {
    return [newItem];
  }

  // Filter out any undefined/null items from breadcrumbs
  const validBc = currentBreadcrumbs.filter(item => item && item.path);

  // Find the same route in breadcrumbs
  const foundItem = validBc.find(item => item.path === newItem.path);
  const index = foundItem ? validBc.indexOf(foundItem) : -1;

  // If back navigation, always slice to found item (don't add new)
  if (back) {
    return index !== -1 ? [...validBc].slice(0, index + 1) : validBc;
  }

  // For forward navigation, add new item or slice to existing
  return index === -1 ? [...validBc, newItem] : [...validBc].slice(0, index + 1);
};

// Initial state
const initialState = {
  breadcrumbs: [],
  currentRoute: null,
  navigationError: null,
  isNavigating: false,
};

// Slice
export const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    // Set breadcrumbs directly
    setBreadcrumbs: (state, action) => {
      state.breadcrumbs = action.payload;
    },

    // Update breadcrumbs with new route
    updateBreadcrumbs: (state, action) => {
      const { route, back } = action.payload;
      const newRoute = route || action.payload; // Support both old and new format
      state.breadcrumbs = getBreadcrumbs(state.breadcrumbs, newRoute, back);
      state.currentRoute = newRoute;
    },

    // Clear breadcrumbs
    clearBreadcrumbs: state => {
      state.breadcrumbs = [];
      state.currentRoute = null;
    },

    // Initialize breadcrumbs from URL
    initBreadcrumbsFromUrl: (state, action) => {
      const { search, pathname } = action.payload;
      state.breadcrumbs = getParsedBreadcrumbs(search, pathname, state.breadcrumbs);
    },

    // Set navigation error
    setNavigationError: (state, action) => {
      state.navigationError = action.payload;
    },

    // Clear navigation error
    clearNavigationError: state => {
      state.navigationError = null;
    },

    // Set navigating flag
    setIsNavigating: (state, action) => {
      state.isNavigating = action.payload;
    },

    // Handle browser back/forward
    handlePopState: (state, action) => {
      const { search, pathname } = action.payload;
      state.breadcrumbs = getParsedBreadcrumbs(search, pathname, state.breadcrumbs);
    },
  },
});

// Actions
export const {
  setBreadcrumbs,
  updateBreadcrumbs,
  clearBreadcrumbs,
  initBreadcrumbsFromUrl,
  setNavigationError,
  clearNavigationError,
  setIsNavigating,
  handlePopState,
} = navigationSlice.actions;

// Export reducer
export default navigationSlice.reducer;
